/**
 * Browser Information Service
 * 
 * This service handles fetching user IP address and generating browser fingerprints
 * using FingerprintJS. It was extracted from the PaymentMethodBox component to 
 * improve separation of concerns and reusability.
 * 
 * Features:
 * - Fetches user IP from external API (ipify.org)
 * - Generates browser fingerprint using FingerprintJS
 * - Provides fallback values when external services fail
 * - Includes validation methods for browser info
 * - Supports parallel fetching for better performance
 */

import axios from 'axios';

export interface BrowserInfo {
  ipAddress: string;
  browserFingerprint: string;
}

export class BrowserInfoService {
  private static readonly FALLBACK_IP = '127.0.0.1';
  private static readonly IP_API_URL = 'https://api.ipify.org?format=json';

  /**
   * Fetches the user's IP address from an external service
   */
  private static async fetchUserIp(): Promise<string> {
    try {
      const response = await axios.get(this.IP_API_URL);
      
      return response.data.ip || this.FALLBACK_IP;
    } catch (error) {
      console.warn('Failed to fetch user IP:', error);
      return this.FALLBACK_IP;
    }
  }

  private static generateFallbackFingerprint(): string {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${random}`;
  }

  /**
   * Fetches both IP address and browser fingerprint
   * Returns a promise that resolves to BrowserInfo object
   */
  static async getBrowserInfo(): Promise<BrowserInfo> {
    try {
      // Fetch both IP and fingerprint in parallel for better performance
      const [ipAddress, browserFingerprint] = await Promise.all([
        this.fetchUserIp(),
        this.generateFallbackFingerprint()
      ]);

      return {
        ipAddress,
        browserFingerprint
      };
    } catch (error) {
      console.error('Failed to get browser info:', error);
      
      // Return fallback values
      return {
        ipAddress: this.FALLBACK_IP,
        browserFingerprint: this.generateFallbackFingerprint()
      };
    }
  }

  /**
   * Validates if the browser info is properly formatted
   */
  static isValidBrowserInfo(info: BrowserInfo): boolean {
    return (
      typeof info.ipAddress === 'string' && 
      info.ipAddress.length > 0 &&
      typeof info.browserFingerprint === 'string' && 
      info.browserFingerprint.length > 0
    );
  }
}
